import React, { useEffect, useState, useCallback } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import { FiSearch, FiCamera, FiChevronLeft, FiChevronRight, FiChevronsLeft, FiChevronsRight } from 'react-icons/fi';
import '../styles/Tickets.css';

/**
 * useDebounce — retorna o valor estabilizado após `delay` ms sem alterações.
 * Enquanto o usuário está digitando, o valor debounced permanece o anterior,
 * evitando re-renders da paginação e perda de foco no campo de busca.
 */
function useDebounce(value, delay = 400) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debouncedValue;
}

// Painel independente para cada evidência. Uma imagem indisponível não impede
// a visualização da outra evidência do mesmo ticket.
const EvidencePanel = ({ label, cameraLabel, timestamp, url }) => {
  const [imageError, setImageError] = useState(false);
  const hasImage = Boolean(url) && !imageError;

  return (
    <section className="evidence-panel" aria-label={`Evidência de ${label.toLowerCase()}`}>
      <div className="evidence-panel-header">
        <div>
          <strong>{label}</strong>
          <span>{cameraLabel}</span>
        </div>
        {timestamp && <time>{timestamp}</time>}
      </div>

      <div className={`evidence-panel-media ${hasImage ? '' : 'is-unavailable'}`}>
        {hasImage ? (
          <img
            src={url}
            alt={`Screenshot da ${label.toLowerCase()}`}
            className="evidence-modal-img"
            decoding="async"
            onError={() => setImageError(true)}
          />
        ) : (
          <div className="evidence-unavailable" role="status">
            <FiCamera size={28} />
            <strong>{url ? 'Imagem não disponível' : `${label} ainda não registrada`}</strong>
            <span>{url ? 'O arquivo de evidência não pôde ser carregado.' : `Este ticket ainda não possui evidência de ${label.toLowerCase()}.`}</span>
          </div>
        )}
      </div>
    </section>
  );
};

// Helper: monta URL do endpoint de evidência com token
function evidenceUrl(imagePath) {
  if (!imagePath) return null;
  const token = localStorage.getItem('token');
  return `/api/evidence/${imagePath}?token=${encodeURIComponent(token)}`;
}

// ─── Mapeamento de forma de pagamento ────────────────────────────────────────
const PAYMENT_LABELS = {
  dinheiro:      { label: 'Dinheiro',         color: '#22c55e' },
  credito:       { label: 'Cartão Crédito',   color: '#3b82f6' },
  debito:        { label: 'Cartão Débito',    color: '#6366f1' },
  pix:           { label: 'PIX',              color: '#8b5cf6' },
  auto_dinheiro: { label: 'Baixa Automática', color: '#f59e0b' },
  isento:        { label: 'Isento',           color: '#94a3b8' },
  sem_parar:     { label: 'Sem Parar',        color: '#00a651' },
};

const STATUS_LABELS = {
  ativo:           { label: 'Ativo',            color: '#22c55e' },
  finalizado:      { label: 'Finalizado',       color: '#64748b' },
  pago:            { label: 'Pago',             color: '#3b82f6' },
  pago_automatico: { label: 'Baixa Automática', color: '#f59e0b' },
  isento:          { label: 'Isento',           color: '#94a3b8' },
  cancelado:       { label: 'Cancelado',        color: '#ef4444' },
};

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

// Data corrente no formato YYYY-MM-DD respeitando o fuso horário local do navegador (BRT/UTC-3).
// toISOString() retorna UTC e causaria data errada após 21h — getFullYear/Month/Date usam hora local.
const _now = new Date();
const todayISO = `${_now.getFullYear()}-${String(_now.getMonth() + 1).padStart(2, '0')}-${String(_now.getDate()).padStart(2, '0')}`;

// ─── Componente de Paginação ──────────────────────────────────────────────────
const Pagination = ({ currentPage, totalPages, pageSize, totalRecords, onPageChange, onPageSizeChange }) => {
  if (totalRecords === 0) return null;

  const startRecord = (currentPage - 1) * pageSize + 1;
  const endRecord   = Math.min(currentPage * pageSize, totalRecords);

  // Gerar array de páginas visíveis (janela de 5 ao redor da página atual)
  const getPageNumbers = () => {
    const delta = 2;
    const range = [];
    const rangeWithDots = [];
    for (let i = Math.max(2, currentPage - delta); i <= Math.min(totalPages - 1, currentPage + delta); i++) {
      range.push(i);
    }
    if (currentPage - delta > 2) rangeWithDots.push(1, '...');
    else rangeWithDots.push(1);
    rangeWithDots.push(...range);
    if (currentPage + delta < totalPages - 1) rangeWithDots.push('...', totalPages);
    else if (totalPages > 1) rangeWithDots.push(totalPages);
    return rangeWithDots;
  };

  return (
    <div className="pagination-wrapper">
      <div className="pagination-info">
        Exibindo <strong>{startRecord}–{endRecord}</strong> de <strong>{totalRecords}</strong> registros
      </div>

      <div className="pagination-controls">
        <button
          className="pagination-btn"
          onClick={() => onPageChange(1)}
          disabled={currentPage === 1}
          title="Primeira página"
        >
          <FiChevronsLeft size={14} />
        </button>
        <button
          className="pagination-btn"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          title="Página anterior"
        >
          <FiChevronLeft size={14} />
        </button>

        {getPageNumbers().map((page, idx) =>
          page === '...' ? (
            <span key={`dots-${idx}`} className="pagination-dots">…</span>
          ) : (
            <button
              key={page}
              className={`pagination-btn ${currentPage === page ? 'active' : ''}`}
              onClick={() => onPageChange(page)}
            >
              {page}
            </button>
          )
        )}

        <button
          className="pagination-btn"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          title="Próxima página"
        >
          <FiChevronRight size={14} />
        </button>
        <button
          className="pagination-btn"
          onClick={() => onPageChange(totalPages)}
          disabled={currentPage === totalPages}
          title="Última página"
        >
          <FiChevronsRight size={14} />
        </button>
      </div>

      <div className="pagination-size-selector">
        <label>Registros por página:</label>
        <select value={pageSize} onChange={e => onPageSizeChange(Number(e.target.value))}>
          {PAGE_SIZE_OPTIONS.map(size => (
            <option key={size} value={size}>{size}</option>
          ))}
        </select>
      </div>
    </div>
  );
};

const Tickets = () => {
  const [tickets, setTickets]               = useState([]);
  const [loading, setLoading]               = useState(true);
  const [evidenceModal, setEvidenceModal]   = useState(null);

  // Fechar o comparador também pelo teclado, sem alterar o fluxo da tabela.
  useEffect(() => {
    if (!evidenceModal) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setEvidenceModal(null);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [evidenceModal]);

  // Paginação
  const [currentPage, setCurrentPage]       = useState(1);
  const [pageSize, setPageSize]             = useState(20);
  // null mantém a ordem padrão; depois alterna saída DESC, saída ASC e volta ao padrão.
  const [exitSortDirection, setExitSortDirection] = useState(null);

  // ─── Separação entre valor digitado e filtro aplicado ────────────────────────
  // `plateInput`     → atualizado a cada keystroke (controla o <input> visualmente)
  // `debouncedPlate` → estabilizado 400ms após a última digitação (dispara fetch)
  // Enquanto o usuário digita, a paginação NÃO é re-renderizada porque
  // `filters` só muda quando debouncedPlate muda — eliminando o desvio de foco.
  const [plateInput, setPlateInput]         = useState('');
  const debouncedPlate                      = useDebounce(plateInput, 400);

  const [filters, setFilters] = useState({
    status: 'todos',
    startDate: todayISO,
    endDate: todayISO
  });

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (debouncedPlate) params.append('plate', debouncedPlate);
      if (filters.status !== 'todos') params.append('status', filters.status);
      // Envia datas com horário completo para cobrir o dia inteiro:
      // start_date + 00:00:00 e end_date + 23:59:59
      if (filters.startDate) params.append('start_date', `${filters.startDate} 00:00:00`);
      if (filters.endDate)   params.append('end_date',   `${filters.endDate} 23:59:59`);
      // A ordenação por saída só é enviada após o clique no cabeçalho.
      if (exitSortDirection) {
        params.append('sort_by', 'exit_time');
        params.append('sort_dir', exitSortDirection);
      }
      // Carrega todos os registros do período (a paginação é feita no cliente)
      params.append('limit', '10000');

      const response = await api.get(`/api/tickets?${params}`);
      setTickets(response.data);
      setCurrentPage(1); // Volta para a primeira página ao recarregar
    } catch (error) {
      toast.error('Erro ao carregar tickets');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedPlate, filters.status, filters.startDate, filters.endDate, exitSortDirection]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  // Resetar para página 1 ao trocar o tamanho
  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setCurrentPage(1);
  };

  const handleExitSortClick = () => {
    setExitSortDirection(current => {
      if (current === null) return 'desc';
      if (current === 'desc') return 'asc';
      return null;
    });
    setCurrentPage(1);
  };

  const exitSortLabel = exitSortDirection === 'desc'
    ? 'Saída: mais recente primeiro'
    : exitSortDirection === 'asc'
      ? 'Saída: mais antiga primeiro'
      : 'Ordem padrão: entrada mais recente primeiro';

  const exitSortIndicator = exitSortDirection === 'desc'
    ? '↓'
    : exitSortDirection === 'asc'
      ? '↑'
      : '↕';

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    const d = new Date(dateString);
    const today     = new Date();
    const yesterday = new Date(); yesterday.setDate(today.getDate() - 1);
    const isSameDay = (a, b) =>
      a.getDate() === b.getDate() &&
      a.getMonth() === b.getMonth() &&
      a.getFullYear() === b.getFullYear();
    const time = d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    if (isSameDay(d, today))     return `Hoje às ${time}`;
    if (isSameDay(d, yesterday)) return `Ontem às ${time}`;
    return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const formatDuration = (minutes) => {
    if (!minutes) return '-';
    const hours = Math.floor(minutes / 60);
    const mins  = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '-';
    return Number(value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  };

  const openEvidenceModal = (ticket) => {
    setEvidenceModal({
      ticketId: ticket.id,
      plate: ticket.plate,
      entry: {
        url: evidenceUrl(ticket.entry_image_path),
        timestamp: formatDateTime(ticket.entry_time),
        cameraLabel: 'Câmera de entrada',
      },
      exit: {
        url: evidenceUrl(ticket.exit_image_path),
        timestamp: ticket.exit_time ? formatDateTime(ticket.exit_time) : null,
        cameraLabel: 'Câmera de saída',
      },
    });
  };

  // ─── Dados paginados ────────────────────────────────────────────────────────
  const totalRecords = tickets.length;
  const totalPages   = Math.max(1, Math.ceil(totalRecords / pageSize));
  const paginatedTickets = tickets.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  if (loading) {
    return <div className="loading">Carregando tickets...</div>;
  }

  return (
    <>
    <div className="tickets-page">
      <h1>Tickets de Estacionamento</h1>

      <div className="filters-section">
        <div className="filter-group">
          <label>Placa</label>
          <div className="search-input">
            <FiSearch />
            <input
              type="text"
              placeholder="Buscar por placa"
              value={plateInput}
              onChange={(e) => setPlateInput(e.target.value.toUpperCase())}
              autoComplete="off"
            />
          </div>
        </div>

        <div className="filter-group">
          <label>Status</label>
          <select
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
          >
            <option value="todos">Todos</option>
            <option value="ativo">Ativo</option>
            <option value="finalizado">Finalizado</option>
            <option value="pago">Pago</option>
            <option value="pago_automatico">Baixa Automática</option>
            <option value="cancelado">Cancelado</option>
          </select>
        </div>

        <div className="filter-group">
          <label>Data Inicial</label>
          <input
            type="date"
            value={filters.startDate}
            onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
          />
        </div>

        <div className="filter-group">
          <label>Data Final</label>
          <input
            type="date"
            value={filters.endDate}
            onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
          />
        </div>
      </div>

      <div className="tickets-table">
        {tickets.length === 0 ? (
          <p className="no-data">Nenhum ticket encontrado</p>
        ) : (
          <>
            {/* Paginação superior */}
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              pageSize={pageSize}
              totalRecords={totalRecords}
              onPageChange={setCurrentPage}
              onPageSizeChange={handlePageSizeChange}
            />

            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Placa</th>
                  <th>Entrada</th>
                  <th
                    className={`sortable-header ${exitSortDirection ? 'is-sorted' : ''}`}
                    aria-sort={exitSortDirection === 'desc' ? 'descending' : exitSortDirection === 'asc' ? 'ascending' : 'none'}
                  >
                    <button
                      type="button"
                      className="table-sort-button"
                      onClick={handleExitSortClick}
                      title={exitSortLabel}
                    >
                      <span>Saída</span>
                      <span className="sort-indicator" aria-hidden="true">{exitSortIndicator}</span>
                    </button>
                  </th>
                  <th>Duração</th>
                  <th>Valor</th>
                  <th>Pagamento</th>
                  <th>Status</th>
                  <th title="Evidências fotográficas de entrada e saída">📷</th>
                </tr>
              </thead>
              <tbody>
                {paginatedTickets.map(ticket => {
                  const statusInfo = STATUS_LABELS[ticket.status] || { label: ticket.status, color: '#64748b' };
                  const payInfo    = ticket.payment_method
                    ? (PAYMENT_LABELS[ticket.payment_method] || { label: ticket.payment_method, color: '#64748b' })
                    : null;
                  return (
                    <tr key={ticket.id}>
                      <td className="ticket-id">#{ticket.id}</td>
                      <td className="plate">{ticket.plate}</td>
                      <td>{formatDateTime(ticket.entry_time)}</td>
                      <td>{formatDateTime(ticket.exit_time)}</td>
                      <td>{formatDuration(ticket.duration_minutes)}</td>
                      <td className="amount">
                        {ticket.amount_due != null
                          ? <span className="amount-value">{formatCurrency(ticket.amount_due)}</span>
                          : <span className="amount-pending">—</span>
                        }
                      </td>
                      <td>
                        {payInfo
                          ? <span className="payment-badge" style={{ background: payInfo.color + '20', color: payInfo.color, border: `1px solid ${payInfo.color}40` }}>
                              {payInfo.label}
                            </span>
                          : <span className="payment-badge payment-none">—</span>
                        }
                      </td>
                      <td>
                        <span className="status-badge" style={{ background: statusInfo.color + '20', color: statusInfo.color, border: `1px solid ${statusInfo.color}40` }}>
                          {statusInfo.label}
                        </span>
                      </td>
                      <td className="evidence-cell">
                        {ticket.entry_image_path || ticket.exit_image_path ? (
                          <button
                            className="btn-evidence"
                            title={ticket.entry_image_path && ticket.exit_image_path
                              ? 'Ver evidências de entrada e saída'
                              : 'Ver evidência fotográfica disponível'}
                            aria-label={`Ver evidências fotográficas do ticket #${ticket.id}`}
                            aria-haspopup="dialog"
                            onClick={() => openEvidenceModal(ticket)}
                          >
                            <FiCamera size={15} />
                            {ticket.entry_image_path && ticket.exit_image_path && <span className="evidence-count">2</span>}
                          </button>
                        ) : (
                          <span className="evidence-none" title="Sem evidência fotográfica">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Paginação inferior */}
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              pageSize={pageSize}
              totalRecords={totalRecords}
              onPageChange={setCurrentPage}
              onPageSizeChange={handlePageSizeChange}
            />
          </>
        )}
      </div>
    </div>

    {/* Modal comparativo de evidências fotográficas */}
    {evidenceModal && (
      <div className="evidence-modal-overlay" onClick={() => setEvidenceModal(null)}>
        <div
          className="evidence-modal evidence-modal-comparison"
          role="dialog"
          aria-modal="true"
          aria-labelledby="evidence-modal-title"
          onClick={e => e.stopPropagation()}
        >
          <div className="evidence-modal-header">
            <div className="evidence-modal-title-group">
              <strong id="evidence-modal-title">📷 Evidências do Ticket #{evidenceModal.ticketId}</strong>
              <span>Placa {evidenceModal.plate}</span>
            </div>
            <button
              className="evidence-modal-close"
              aria-label="Fechar evidências"
              onClick={() => setEvidenceModal(null)}
            >
              ✕
            </button>
          </div>

          <div className="evidence-grid">
            <EvidencePanel key={`${evidenceModal.ticketId}-entry`} {...evidenceModal.entry} label="Entrada" />
            <EvidencePanel key={`${evidenceModal.ticketId}-exit`} {...evidenceModal.exit} label="Saída" />
          </div>

          <div className="evidence-modal-caption">
            Clique fora do modal ou pressione Esc para fechar
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default Tickets;
